import qz from 'qz-tray';
import JsBarcode from 'jsbarcode';
import { toast } from 'sonner';

// Zebra Browser Print — direct HTTP to local service
let zebraDevice = null;

const connectZebraBrowserPrint = async () => {
  // Try HTTPS first (port 9101), then HTTP (port 9100)
  const urls = ['https://localhost:9101/', 'http://localhost:9100/'];
  for (const baseUrl of urls) {
    try {
      const res = await fetch(baseUrl + 'default?type=printer', { method: 'GET' });
      if (res.ok) {
        const text = await res.text();
        // Parse the device info from response
        if (text && text.trim().length > 0) {
          zebraDevice = { url: baseUrl, raw: text };
          console.log('[ZEBRA] Connected via Browser Print at', baseUrl);
          return true;
        }
      }
    } catch (e) {
      console.warn('[ZEBRA] Cannot reach', baseUrl, e.message);
    }
  }
  return false;
};

const sendZPLViaZebraBrowserPrint = async (zplData) => {
  if (!zebraDevice) {
    const ok = await connectZebraBrowserPrint();
    if (!ok) return false;
  }
  try {
    const res = await fetch(zebraDevice.url + 'write', {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      body: JSON.stringify({ device: zebraDevice.raw, data: zplData })
    });
    if (res.ok) {
      console.log('[ZEBRA] ZPL sent via Browser Print');
      return true;
    }
    console.warn('[ZEBRA] Write failed:', res.status);
    return false;
  } catch (e) {
    console.error('[ZEBRA] Print failed:', e);
    zebraDevice = null;
    return false;
  }
};

// Auto-detect barcode format: EAN13 for 13 digits, UPC for 12 digits, CODE128 for others
const detectBarcodeFormat = (value) => {
  if (!value) return 'CODE128';
  const clean = value.trim();
  if (/^\d{13}$/.test(clean)) return 'EAN13';
  if (/^\d{12}$/.test(clean)) return 'UPC';
  return 'CODE128';
};

// Generate barcode as PNG data URL
export const generateBarcodePNG = (value) => {
  if (!value) return '';
  const canvas = document.createElement('canvas');
  const format = detectBarcodeFormat(value);
  try {
    JsBarcode(canvas, value.trim(), { format, width: 1.5, height: 30, displayValue: false, margin: 0 });
    return canvas.toDataURL('image/png');
  } catch (e) {
    // Fallback to CODE128 if format-specific encoding fails
    try {
      JsBarcode(canvas, value.trim(), { format: 'CODE128', width: 1.5, height: 30, displayValue: false, margin: 0 });
      return canvas.toDataURL('image/png');
    } catch {
      console.warn('Barcode generation failed for:', value);
      return '';
    }
  }
};

// Printer configuration - maps print job types to printer names
const PRINTER_MAP = {
  receipt: null,  // Will be set from HardwareSettings
  label: null,
  document: null,
};

let connected = false;

// Connect to QZ Tray
export const connectQZ = async () => {
  if (connected && qz.websocket.isActive()) return true;
  // Reset flag if websocket dropped
  connected = false;
  try {
    await qz.websocket.connect({
      host: 'localhost',
      usingSecure: true,
      retries: 2,
      delay: 1,
    });
    connected = true;
    console.log('[QZ] Connected successfully');
    return true;
  } catch (err) {
    // If already open, treat as connected
    if (err && err.message && err.message.includes('already')) {
      connected = true;
      return true;
    }
    const msg = (err && (err.message || err.toString())) || 'unknown error';
    console.error('[QZ] Connect failed:', msg, err);
    // Surface the real error to the user so we can diagnose
    try { toast.error(`QZ connect failed: ${msg}`, { duration: 8000 }); } catch {}
    return false;
  }
};

// Disconnect
export const disconnectQZ = async () => {
  if (qz.websocket.isActive()) {
    await qz.websocket.disconnect();
    connected = false;
  }
};

// Get list of all printers
export const listPrinters = async () => {
  const ok = await connectQZ();
  if (!ok) return [];
  try {
    return await qz.printers.find();
  } catch {
    return [];
  }
};

// Set printer for a job type
export const setPrinter = (jobType, printerName) => {
  PRINTER_MAP[jobType] = printerName;
  // Save to localStorage
  const saved = JSON.parse(localStorage.getItem('printerMap') || '{}');
  saved[jobType] = printerName;
  localStorage.setItem('printerMap', JSON.stringify(saved));
};

// Load saved printer config
export const loadPrinterConfig = () => {
  try {
    const saved = JSON.parse(localStorage.getItem('printerMap') || '{}');
    if (saved.receipt) PRINTER_MAP.receipt = saved.receipt;
    if (saved.label) PRINTER_MAP.label = saved.label;
    if (saved.document) PRINTER_MAP.document = saved.document;
  } catch (e) {
    console.warn('PrinterService: failed to load printerMap from localStorage', e);
  }
  return { ...PRINTER_MAP };
};

// Auto-load saved printer config the moment this module is imported, so any
// page (POS, SmartPO, etc.) can print without first opening Hardware Settings.
// Without this, PRINTER_MAP stays null on a fresh browser session.
if (typeof window !== 'undefined' && window.localStorage) {
  loadPrinterConfig();
}

// ==================== BROWSER PRINT MODE (QZ-LESS FALLBACK) ====================
// When the user enables this in Hardware Settings (e.g. at Eastridge where QZ Tray
// keeps disconnecting), every print job uses the browser's native print dialog
// instead of QZ Tray. Staff can then manually pick the Star receipt printer or
// Zebra label printer from the OS print dialog dropdown.
export const getBrowserPrintMode = () => {
  try { return localStorage.getItem('force_browser_print') === '1'; } catch { return false; }
};
export const setBrowserPrintMode = (on) => {
  try {
    if (on) localStorage.setItem('force_browser_print', '1');
    else localStorage.removeItem('force_browser_print');
  } catch {}
  return getBrowserPrintMode();
};

// QZ Tray is OFF by default for labels — browser print is the standard since QZ
// has been dropping connections. To opt back in (rare), set localStorage key
// `qz_preferred=1` in Hardware Settings. When false, label printing always uses
// the browser dialog.
export const getQzPreferred = () => {
  try { return localStorage.getItem('qz_preferred') === '1'; } catch { return false; }
};
export const setQzPreferred = (on) => {
  try {
    if (on) localStorage.setItem('qz_preferred', '1');
    else localStorage.removeItem('qz_preferred');
  } catch {}
  return getQzPreferred();
};

// Open an off-screen iframe, write the HTML into it, trigger the native print
// dialog, then clean up. Native dialog lets the user pick ANY installed printer.
const _printHtmlViaBrowser = (html, label = 'print') => {
  return new Promise((resolve) => {
    try {
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      document.body.appendChild(iframe);
      const doc = iframe.contentDocument || iframe.contentWindow.document;
      doc.open();
      doc.write(html);
      doc.close();
      // Give the browser a tick to lay out, then fire print + cleanup.
      setTimeout(() => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } catch (e) {
          console.error('[BROWSER PRINT] failed', e);
          toast.error(`${label} print failed: ${e.message}`);
        }
        // Remove the iframe shortly after the dialog closes.
        setTimeout(() => {
          try { document.body.removeChild(iframe); } catch {}
          resolve();
        }, 1500);
      }, 250);
    } catch (e) {
      toast.error(`${label} print failed: ${e.message}`);
      resolve();
    }
  });
};

// Get printer name for job type
export const getPrinter = (jobType) => {
  return PRINTER_MAP[jobType];
};

// Helper: right-pad a line so left and right text fill 46 chars (Star TSP 80mm
// with a small safety margin on the right so prices don't get clipped/wrapped).
const receiptLine = (left, right, width = 46) => {
  const maxLeft = width - right.length - 1;
  const l = left.length > maxLeft ? left.substring(0, maxLeft) : left;
  return l + ' '.repeat(width - l.length - right.length) + right + '\n';
};

const ESC = '\x1B';
const GS = '\x1D';
const COMMANDS = {
  INIT: ESC + '\x40',
  CENTER: ESC + '\x61\x01',
  LEFT: ESC + '\x61\x00',
  BOLD_ON: ESC + '\x45\x01',
  BOLD_OFF: ESC + '\x45\x00',
  // Star Line Mode double-height/width commands (compatible with both Star Line
  // Mode and ESC/POS). The previous GS-! based commands printed a literal "!" on
  // Star Line Mode printers, which also pushed receipt lines past 48 chars and
  // caused the TOTAL value to wrap.
  DOUBLE_HW: ESC + '\x69\x01\x01',     // double height AND width
  DOUBLE_H:  ESC + '\x69\x01\x00',     // double height only
  NORMAL:    ESC + '\x69\x00\x00',     // back to normal size
  // Make every dot print twice → much darker, crisper output.
  DOUBLE_STRIKE_ON:  ESC + '\x47\x01',
  DOUBLE_STRIKE_OFF: ESC + '\x47\x00',
  CUT: GS + '\x56\x41\x03',
  // Cash drawer kick — Star printers default to "Star Line Mode" which uses
  // BEL (\x07) for drawer 1, while ESC/POS uses "ESC p 0 m n". Sending both
  // commands so the drawer fires regardless of which mode the printer is in.
  // The printer silently ignores whichever one doesn't apply.
  DRAWER: '\x07' + ESC + '\x07' + ESC + '\x70\x00\x32\x32',
};
const DASHES = '----------------------------------------------\n';

// Build ESC/POS raw data array from receipt object
const buildReceiptRaw = (r) => {
  const d = [];
  d.push(COMMANDS.INIT);
  // Turn on double-strike for the whole receipt → darker print, easier to read.
  d.push(COMMANDS.DOUBLE_STRIKE_ON);
  d.push(COMMANDS.CENTER);

  // Store header
  d.push(COMMANDS.BOLD_ON + COMMANDS.DOUBLE_HW);
  d.push((localStorage.getItem('business_name') || 'SupplyTrack').toUpperCase() + '\n');
  d.push(COMMANDS.NORMAL);
  if (r.store_name) { d.push(COMMANDS.BOLD_ON); d.push(r.store_name.toUpperCase() + '\n'); d.push(COMMANDS.BOLD_OFF); }
  if (r.store_phone) d.push('Ph: ' + r.store_phone + '\n');
  if (r.gst_number) d.push('GST: ' + r.gst_number + '\n');
  d.push(r.isReprint ? 'TAX INVOICE (REPRINT)\n' : 'TAX INVOICE\n');
  d.push(DASHES);

  // Date / receipt ID / customer
  d.push(COMMANDS.LEFT);
  const dateStr = r.created_at ? new Date(r.created_at).toLocaleString('en-NZ') : new Date().toLocaleString('en-NZ');
  d.push(dateStr + ' | ' + (r.id?.slice(-8).toUpperCase() || '') + '\n');
  if (r.customer_name && r.customer_name !== 'Walk-in Customer') {
    d.push('Customer: ' + r.customer_name + '\n');
  }
  d.push(DASHES);

  // Line items
  for (const item of (r.items || [])) {
    const lineTotal = '$' + (item.quantity * item.unit_price).toFixed(2);
    d.push(receiptLine(item.quantity + 'x ' + item.product_name, lineTotal));
    if (item.is_wholesale && item.original_retail_price) {
      const savings = ((item.original_retail_price - item.unit_price) * item.quantity).toFixed(2);
      d.push('  RRP $' + item.original_retail_price.toFixed(2) + ' | Save $' + savings + '\n');
    }
  }

  // Wholesale summary
  const wholesaleItems = (r.items || []).filter(i => i.is_wholesale);
  if (wholesaleItems.length > 0) {
    const totalSavings = wholesaleItems.reduce((sum, i) => sum + (i.original_retail_price - i.unit_price) * i.quantity, 0);
    d.push(DASHES);
    d.push(COMMANDS.BOLD_ON);
    d.push(receiptLine('WHOLESALE DISCOUNT', '-$' + totalSavings.toFixed(2)));
    d.push(COMMANDS.BOLD_OFF);
  }
  d.push(DASHES);

  // Subtotal & GST
  d.push(receiptLine('Subtotal (Ex GST)', '$' + r.subtotal.toFixed(2)));
  d.push(receiptLine('GST (15%)', '$' + r.tax_amount.toFixed(2)));

  // Discounts
  if (r.points_discount > 0) d.push(receiptLine('Loyalty Credit', '-$' + r.points_discount.toFixed(2)));
  if (r.gift_card_discount > 0) d.push(receiptLine('Gift Card (' + (r.gift_card_code || '') + ')', '-$' + r.gift_card_discount.toFixed(2)));
  if (r.store_credit_used > 0 && r.payment_method !== 'store_credit') d.push(receiptLine('Store Credit', '-$' + r.store_credit_used.toFixed(2)));
  if (r.manual_discount > 0) {
    const label = r.manual_discount_type === 'percent'
      ? 'Discount (' + r.manual_discount_value + '%)'
      : 'Discount';
    d.push(receiptLine(label, '-$' + r.manual_discount.toFixed(2)));
  }
  d.push(DASHES);

  // Total
  d.push(COMMANDS.BOLD_ON + COMMANDS.DOUBLE_H);
  d.push(receiptLine('TOTAL', '$' + r.total_amount.toFixed(2)));
  d.push(COMMANDS.NORMAL + COMMANDS.BOLD_OFF);
  d.push(DASHES);

  // Payment
  const method = (r.payment_method || '').toUpperCase();
  d.push(receiptLine(method, '$' + r.total_amount.toFixed(2)));
  if (r.payment_method === 'cash' && r.cash_received != null) {
    d.push(receiptLine('Cash', '$' + Number(r.cash_received).toFixed(2)));
    d.push(COMMANDS.BOLD_ON);
    d.push(receiptLine('Change', '$' + Number(r.change_given).toFixed(2)));
    d.push(COMMANDS.BOLD_OFF);
  }
  if (r.split_payment) {
    d.push(receiptLine('Cash', '$' + Number(r.cash_amount).toFixed(2)));
    d.push(receiptLine('Card', '$' + Number(r.card_amount).toFixed(2)));
  }

  // Loyalty Credit — single line showing only the current balance
  if ((r.points_balance || 0) > 0 || r.points_earned > 0 || r.points_discount > 0) {
    d.push(DASHES);
    d.push(COMMANDS.BOLD_ON);
    d.push(receiptLine('Loyalty balance', '$' + (r.points_balance || 0)));
    d.push(COMMANDS.BOLD_OFF);
  }
  d.push(DASHES);

  // Footer
  d.push(COMMANDS.CENTER);
  d.push('Thank you for shopping with us!\n');
  d.push('\n');
  // Cash drawer: pop the drawer ONLY for cash or split-cash payments. The
  // command is appended BEFORE the paper cut so the kick fires reliably.
  if (r.payment_method === 'cash' || r.split_payment) {
    d.push(COMMANDS.DRAWER);
  }
  d.push(COMMANDS.CUT);

  return d;
};

// Build fallback HTML from receipt data.
// Designed for 80mm thermal roll on a Star TSP143/650 via browser print.
// Set Star printer "Page Layout" → "80(72mm) Receipt" in Windows printer prefs.
const buildReceiptHTML = (r) => {
  const store = r.store_name ? r.store_name.toUpperCase() : '';
  const dateStr = r.created_at ? new Date(r.created_at).toLocaleString('en-NZ', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : new Date().toLocaleString('en-NZ');
  const items = (r.items || []).map(i => {
    const disc = Number(i.line_discount || 0);
    const lineTotal = (Number(i.quantity) * Number(i.unit_price) - disc).toFixed(2);
    const discNote = disc > 0 ? `<div class="line" style="font-size:10px;color:#555"><span class="nm">&nbsp;&nbsp;line discount</span><span class="amt">-$${disc.toFixed(2)}</span></div>` : '';
    return `<div class="line"><span class="qty">${i.quantity}×</span> <span class="nm">${i.product_name}</span><span class="amt">$${lineTotal}</span></div>${discNote}`;
  }).join('');

  return `<!DOCTYPE html><html><head><title>Receipt</title>
  <style>
    @page { margin: 0; size: 80mm auto; }
    @media print {
      html, body { width: 72mm; margin: 0 !important; padding: 0 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
    * { box-sizing: border-box; }
    body { font-family: 'Courier New', Courier, monospace; font-size: 11pt; line-height: 1.35; color: #000; width: 72mm; margin: 0 auto; padding: 3mm 2mm; }
    .center { text-align: center; }
    .bold { font-weight: bold; }
    .store-name { font-size: 14pt; font-weight: bold; letter-spacing: 0.5px; margin-bottom: 1mm; }
    .store-branch { font-size: 12pt; font-weight: bold; margin-bottom: 1mm; }
    .addr, .ph, .meta { font-size: 9.5pt; }
    .invoice-label { font-size: 10pt; font-weight: bold; margin: 1.5mm 0 0.5mm 0; }
    hr.dashed { border: 0; border-top: 1px dashed #000; margin: 2mm 0; }
    .line { display: flex; justify-content: space-between; align-items: flex-start; font-size: 10.5pt; gap: 1mm; margin: 0.5mm 0; }
    .line .qty { flex: 0 0 auto; font-weight: bold; }
    .line .nm { flex: 1 1 auto; padding: 0 1mm; word-break: break-word; }
    .line .amt { flex: 0 0 auto; white-space: nowrap; font-weight: bold; }
    .row { display: flex; justify-content: space-between; font-size: 10.5pt; margin: 0.5mm 0; }
    .total-row { font-size: 13pt; font-weight: bold; margin: 1mm 0; }
    .thanks { text-align: center; font-size: 10.5pt; margin-top: 2mm; font-weight: bold; }
    .footer-note { text-align: center; font-size: 9.5pt; margin-top: 1mm; font-style: italic; }
  </style></head><body>
    <div class="center store-name">${(localStorage.getItem('business_name') || 'SupplyTrack').toUpperCase()}</div>
    ${store ? `<div class="center store-branch">${store}</div>` : ''}
    ${r.store_address ? `<div class="center addr">${r.store_address}</div>` : ''}
    ${r.store_phone ? `<div class="center ph">Ph: ${r.store_phone}</div>` : ''}
    ${r.gst_number ? `<div class="center meta">GST: ${r.gst_number}</div>` : ''}
    <div class="center invoice-label">${r.isReprint ? 'TAX INVOICE (REPRINT)' : 'TAX INVOICE'}</div>
    <div class="center meta">${dateStr}</div>
    <div class="center meta">Receipt #: ${(r.id || '').slice(-8).toUpperCase()}</div>
    ${r.customer_name && r.customer_name !== 'Walk-in Customer' ? `<div class="center meta">Customer: ${r.customer_name}</div>` : ''}
    <hr class="dashed"/>
    ${items}
    <hr class="dashed"/>
    <div class="row"><span>Subtotal (Ex GST)</span><span>$${r.subtotal.toFixed(2)}</span></div>
    <div class="row"><span>GST (15%)</span><span>$${r.tax_amount.toFixed(2)}</span></div>
    ${r.points_discount > 0 ? `<div class="row"><span>Loyalty Credit</span><span>-$${r.points_discount.toFixed(2)}</span></div>` : ''}
    ${r.gift_card_discount > 0 ? `<div class="row"><span>Gift Card</span><span>-$${r.gift_card_discount.toFixed(2)}</span></div>` : ''}
    ${r.store_credit_used > 0 && r.payment_method !== 'store_credit' ? `<div class="row"><span>Store Credit</span><span>-$${r.store_credit_used.toFixed(2)}</span></div>` : ''}
    ${r.manual_discount > 0 ? `<div class="row"><span>Discount${r.manual_discount_type === 'percent' ? ` (${r.manual_discount_value}%)` : ''}</span><span>-$${r.manual_discount.toFixed(2)}</span></div>` : ''}
    <hr class="dashed"/>
    <div class="row total-row"><span>TOTAL (Inc GST)</span><span>$${r.total_amount.toFixed(2)}</span></div>
    <hr class="dashed"/>
    <div class="row"><span>Payment</span><span class="bold">${(r.payment_method || '').toUpperCase()}</span></div>
    ${r.payment_method === 'cash' && r.cash_received != null ? `<div class="row"><span>Cash tendered</span><span>$${Number(r.cash_received).toFixed(2)}</span></div><div class="row bold"><span>Change</span><span>$${Number(r.change_given).toFixed(2)}</span></div>` : ''}
    ${r.split_payment ? `<div class="row"><span>Cash</span><span>$${Number(r.cash_amount).toFixed(2)}</span></div><div class="row"><span>Card</span><span>$${Number(r.card_amount).toFixed(2)}</span></div>` : ''}
    ${(r.points_earned > 0 || r.points_discount > 0 || (r.points_balance || 0) > 0) ? `
      <hr class="dashed"/>
      ${r.points_earned > 0 ? `<div class="row"><span>Earned credit</span><span>+$${r.points_earned}</span></div>` : ''}
      <div class="row bold"><span>Loyalty balance</span><span>$${r.points_balance || 0}</span></div>` : ''}
    <hr class="dashed"/>
    <div class="thanks">Thank you for your purchase!</div>
    <div class="footer-note">thewellnessstore.co.nz</div>
    <div style="height:6mm"></div>
  </body></html>`;
};

// Print receipt to Star TSP143IIU using RAW ESC/POS commands.
// If Browser Print Mode is on (or QZ Tray is down), falls back to the browser's
// native print dialog so staff can pick the printer manually.
export const printReceipt = async (receiptData) => {
  // DEFAULT — go straight to browser print. QZ is unstable; staff pick the Star
  // receipt printer in the OS dialog the first time and Chrome remembers it.
  if (!getQzPreferred() || getBrowserPrintMode()) {
    await _printHtmlViaBrowser(buildReceiptHTML(receiptData), 'Receipt');
    return;
  }

  const ok = await connectQZ();
  if (!ok) {
    toast.warning('QZ Tray not connected — falling back to browser print. Pick the Star printer in the dialog.');
    await _printHtmlViaBrowser(buildReceiptHTML(receiptData), 'Receipt');
    return;
  }

  const printerName = PRINTER_MAP.receipt;
  if (!printerName) {
    toast.warning('No receipt printer set in Hardware Settings — using browser print. Pick the Star printer in the dialog.');
    await _printHtmlViaBrowser(buildReceiptHTML(receiptData), 'Receipt');
    return;
  }

  try {
    const config = qz.configs.create(printerName);
    const rawData = buildReceiptRaw(receiptData);
    await qz.print(config, rawData);
  } catch (err) {
    console.error('QZ receipt print failed:', err);
    toast.warning('QZ Tray failed — falling back to browser print. Pick the Star printer in the dialog.');
    await _printHtmlViaBrowser(buildReceiptHTML(receiptData), 'Receipt');
  }
};

// ==================== STANDALONE CASH-DRAWER KICK ====================
// Fires the drawer kick command WITHOUT printing a receipt. Useful for the
// "Open Drawer" button + when the receipt printer is the only thing connected
// to the drawer cable.
export const openCashDrawer = async () => {
  // If browser-print mode (no QZ) — there's literally no way to send raw
  // ESC/POS from a browser. Tell the user and fail soft.
  if (!getQzPreferred() || getBrowserPrintMode()) {
    toast.error('Cash drawer needs QZ Tray. Enable it in Hardware Settings → Use QZ when possible, then set up your Star receipt printer.', { duration: 8000 });
    return false;
  }
  const ok = await connectQZ();
  if (!ok) {
    toast.error('QZ Tray not connected. Start QZ Tray on this PC first.');
    return false;
  }
  const printerName = PRINTER_MAP.receipt;
  if (!printerName) {
    toast.error('Set your receipt printer in Hardware Settings first.');
    return false;
  }
  try {
    const config = qz.configs.create(printerName);
    // Send BOTH drawer-kick commands so it works on Star Line Mode AND ESC/POS
    await qz.print(config, [COMMANDS.INIT, COMMANDS.DRAWER]);
    return true;
  } catch (err) {
    console.error('Drawer kick failed:', err);
    toast.error(`Drawer kick failed: ${err.message || err}`);
    return false;
  }
};

// ==================== ZEBRA LABEL PRINTING (ZPL) ====================

// Generate ZPL for a ROW of 3 labels across on 90mm stock (GC420d, 203 DPI)
// Each label = 30mm (240 dots) wide, 22mm (176 dots) tall
const generateRowZPL = (products) => {
  const LW = 240; // Single label width in dots (30mm)

  let zpl = '^XA';
  zpl += '^LH0,0';             // Label Home: force origin to absolute top-left
  zpl += '^LT0';               // Label Top: no offset (reset any stored offset)
  zpl += '^LS0';               // Label Shift: no horizontal shift
  zpl += '^PW720';             // Print Width: 90mm = 720 dots (3 labels across)
  zpl += '^LL176';             // Label Length: 22mm = 176 dots
  zpl += '^CI28';              // UTF-8
  zpl += '^MD15';              // Media Darkness: +15 for crisp, bold ink (range -30 to +30)
  zpl += '^PR2';               // Print Rate: 2 ips (slower = darker, more accurate)

  products.forEach((product, idx) => {
    const x = idx * LW;        // x offset: 0, 240, or 480
    const name = (product.name || '').toUpperCase();
    const barcode = (product.barcode || '').trim();
    const price = '$' + parseFloat(product.sell_price || product.price || 0).toFixed(2);
    const tag = (product.tag || '').toUpperCase();
    const wasPrice = product.was_price ? '$' + parseFloat(product.was_price).toFixed(2) : '';

    // Top line: store name, or the short-dated tag ("20% OFF · EXP 09/26") when present
    if (tag) {
      zpl += `^FO${x},2^A0N,13,13^FB${LW},1,0,C,0^FD${tag}^FS`;
    } else {
      zpl += `^FO${x},2^A0N,10,10^FB${LW},1,0,C,0^FD${(localStorage.getItem('business_name') || 'SupplyTrack').toUpperCase()}^FS`;
    }

    // Product name — BIGGER + BOLDER, auto-wrapped to 2 lines via ZPL ^FB
    // ^A0N at 18 high × 14 wide is the readable "bold-looking" size on a 203dpi head.
    // ^FB ${LW},2,...  → field block: max width LW, up to 2 lines, word-wrap, centred.
    zpl += `^FO${x},16^A0N,18,14^FB${LW},2,2,C,0^FD${name}^FS`;

    // Barcode (Y=68) — bigger height (45 dots) for easier scanning, properly centred
    if (barcode) {
      if (/^\d{12}$/.test(barcode)) {
        // UPC-A at ^BY2 is ~190 dots wide → centre at (240-190)/2 = 25
        zpl += `^FO${x + 25},68^BY2^BUN,45,Y,N^FD${barcode.slice(0, 11)}^FS`;
      } else if (/^\d{13}$/.test(barcode)) {
        // EAN-13 at ^BY2 is ~213 dots wide → centre at (240-213)/2 = 13
        // (was x+20 which pushed right edge off the rightmost label)
        zpl += `^FO${x + 13},68^BY2^BEN,45,Y,N^FD${barcode.slice(0, 12)}^FS`;
      } else {
        // Code128 — used for short/non-EAN codes like 8-digit internal SKUs.
        // ^BY2 (was ^BY1) gives 0.25mm bar width — many laser scanners
        // refuse to read narrower bars, which was why staff-printed
        // 94-prefix labels weren't scanning. 8-digit Code-C codes are
        // ~160 dots wide at ^BY2 → fits comfortably in 240-dot label.
        zpl += `^FO${x + 30},68^BY2^BCN,45,Y,N,N^FD${barcode}^FS`;
      }
    }

    // Price (Y=140, bigger and lower for better visibility); short-dated adds "was $X" alongside
    if (wasPrice) {
      zpl += `^FO${x},140^A0N,24,24^FB${LW - 90},1,0,R,0^FD${price}^FS`;
      zpl += `^FO${x + LW - 86},147^A0N,14,12^FB84,1,0,L,0^FDwas ${wasPrice}^FS`;
    } else {
      zpl += `^FO${x},140^A0N,24,24^FB${LW},1,0,C,0^FD${price}^FS`;
    }
  });

  zpl += '^XZ';
  return zpl;
};

// Generate ZPL for ONE individual 30mm x 22mm label (single product)
const generateSingleLabelZPL = (product) => {
  return generateRowZPL([product]);
};

// Build fallback HTML for browser printing (when QZ Tray not available)
// Groups 3 labels per row to match 90mm x 22mm 3-across label stock (Zebra GC420d).
//
// CRITICAL: Chrome will shrink 90×22mm content onto A4 unless the user picks the
// matching paper size in the print dialog (More Settings → Paper size → 90×22mm).
// We force the page size and scale via CSS so once the Zebra is selected as the
// printer, Chrome auto-snaps to the correct stock.
const buildLabelHTML = (labels) => {
  const rows = [];
  for (let i = 0; i < labels.length; i += 3) {
    rows.push(labels.slice(i, i + 3));
  }
  return `<html><head><title>Labels</title>
    <style>
      @page { margin: 0; size: 90mm 22mm; }
      @media print {
        html, body { width: 90mm; height: 22mm; margin: 0 !important; padding: 0 !important; }
        .row { page-break-after: always; page-break-inside: avoid; break-after: page; }
        .row:last-child { page-break-after: auto; }
      }
      * { margin: 0; padding: 0; box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      body { font-family: Arial, Helvetica, sans-serif; margin: 0; padding: 0; }
      .row { width: 90mm; height: 22mm; display: flex; flex-direction: row; }
      .label { width: 30mm; height: 22mm; display: flex; flex-direction: column; align-items: center; justify-content: flex-start; text-align: center; padding: 0.5mm; overflow: hidden; }
      .store { font-size: 4pt; font-weight: normal; text-transform: uppercase; line-height: 1; letter-spacing: 0.2px; }
      .name { font-size: 6.5pt; font-weight: bold; text-transform: uppercase; line-height: 1.05; max-height: 5mm; overflow: hidden; word-break: break-word; margin: 0.2mm 0; }
      .barcode-img { width: 28mm; height: 7mm; object-fit: contain; display: block; }
      .barcode-num { font-size: 5.5pt; font-family: monospace; line-height: 1; }
      .price { font-size: 10pt; font-weight: bold; line-height: 1; margin-top: 0.4mm; }
      .tag { font-size: 5pt; font-weight: bold; text-transform: uppercase; line-height: 1; color: #000; }
      .was { font-size: 5pt; font-weight: normal; color: #333; margin-left: 1mm; }
    </style></head><body>
    ${rows.map(row => `<div class="row">
      ${row.map(p => `<div class="label">
        ${p.tag ? `<div class="tag">${p.tag}</div>` : `<div class="store">${localStorage.getItem('business_name') || 'SupplyTrack'}</div>`}
        <div class="name">${p.name}</div>
        ${p.barcodeImg ? `<img class="barcode-img" src="${p.barcodeImg}" />` : ''}
        ${p.barcode ? `<div class="barcode-num">${p.barcode}</div>` : ''}
        <div class="price">$${parseFloat(p.sell_price || p.price || 0).toFixed(2)}${p.was_price ? `<span class="was">was $${parseFloat(p.was_price).toFixed(2)}</span>` : ''}</div>
      </div>`).join('')}
    </div>`).join('')}
    </body></html>`;
};

// Send labels — DEFAULT path is browser print (fast, reliable, never drops).
// QZ Tray is unstable in this environment so we don't even try it unless the user
// opts back in via Hardware Settings.
const sendLabelsToZebra = async (products) => {
  const browserFallback = async (msg) => {
    if (msg) toast.info(msg);
    const enriched = products.map(p => ({
      ...p,
      barcodeImg: p.barcode ? generateBarcodePNG(p.barcode) : '',
    }));
    await _printHtmlViaBrowser(buildLabelHTML(enriched), 'Label');
  };

  // DEFAULT — go straight to OS print dialog. Staff pick the Zebra printer the
  // first time and Chrome remembers it forever. No QZ wait, no drops.
  if (!getQzPreferred() || getBrowserPrintMode()) {
    await browserFallback('Opening browser print — pick your Zebra label printer in the dialog.');
    return;
  }

  // ---- "Use QZ when possible" path (only if user opted in via Hardware Settings) ----
  const rows = [];
  for (let i = 0; i < products.length; i += 3) {
    rows.push(products.slice(i, i + 3));
  }
  const zplBlocks = rows.map(row => generateRowZPL(row));
  const allZPL = zplBlocks.join('\n');

  const zebraOK = await sendZPLViaZebraBrowserPrint(allZPL);
  if (zebraOK) {
    console.log('[PRINT] Labels sent via Zebra Browser Print (RAW ZPL)');
    return;
  }

  const qzOK = await connectQZ();
  if (qzOK && PRINTER_MAP.label) {
    try {
      const config = qz.configs.create(PRINTER_MAP.label, { encoding: 'UTF-8' });
      const rawJobs = zplBlocks.map(z => ({ type: 'raw', format: 'command', data: z, flavor: 'plain' }));
      console.log('[PRINT] Labels sent via QZ Tray (RAW ZPL),', zplBlocks.length, 'row(s)');
      await qz.print(config, rawJobs);
      return;
    } catch (qzErr) {
      console.warn('[PRINT] QZ label send failed:', qzErr.message);
      await browserFallback('QZ Tray send failed — switching to browser print.');
      return;
    }
  }

  await browserFallback('QZ Tray / Zebra Browser Print unavailable — using browser print.');
};

// ==================== LABEL QUEUE (stack 3-up so no label positions are wasted) ====================
// Labels accumulate until a full row of 3 is ready. Full rows print immediately;
// leftovers wait (persisted across pages/reloads) until 1-2 more labels arrive,
// or staff taps "Print now" on the toast to flush a partial row.
const LABEL_QUEUE_KEY = 'label_print_queue_v1';

const loadLabelQueue = () => {
  try { return JSON.parse(localStorage.getItem(LABEL_QUEUE_KEY)) || []; } catch { return []; }
};
const saveLabelQueue = (q) => {
  try { localStorage.setItem(LABEL_QUEUE_KEY, JSON.stringify(q)); } catch { /* ignore */ }
};

export const getLabelQueueCount = () => loadLabelQueue().length;

export const flushLabelQueue = async () => {
  const q = loadLabelQueue();
  if (q.length === 0) { toast.info('No labels waiting'); return; }
  saveLabelQueue([]);
  await sendLabelsToZebra(q);
};

const _slimLabel = (p) => ({
  name: p.name || p.product_name || '',
  barcode: p.barcode || '',
  sell_price: p.sell_price || p.price || 0,
  ...(p.tag ? { tag: p.tag } : {}),
  ...(p.was_price ? { was_price: p.was_price } : {}),
});

const queueAndPrintLabels = async (labels) => {
  const q = [...loadLabelQueue(), ...labels.map(_slimLabel)];
  const fullRows = Math.floor(q.length / 3) * 3;
  const toPrint = q.slice(0, fullRows);
  const remainder = q.slice(fullRows);
  saveLabelQueue(remainder);
  if (toPrint.length > 0) {
    await sendLabelsToZebra(toPrint);
    toast.success(`Printing ${toPrint.length} label${toPrint.length > 1 ? 's' : ''} (${toPrint.length / 3} full row${toPrint.length > 3 ? 's' : ''})`);
  }
  if (remainder.length > 0) {
    const need = 3 - remainder.length;
    toast(`${remainder.length} label${remainder.length > 1 ? 's' : ''} waiting — ${need} more fills the row`, {
      duration: 10000,
      description: 'Queued labels print automatically when the row of 3 is full.',
      action: { label: 'Print now', onClick: () => flushLabelQueue() },
    });
  }
};

// Print labels for products (single or multiple) - 1 label per product
export const printProductLabels = async (products) => {
  try {
    await queueAndPrintLabels(products);
  } catch (err) {
    console.error('Label print error:', err);
    alert('Label printing failed: ' + err.message);
  }
};

// Batch label printing with copies support
// items: [{name, barcode, sell_price, copies}]
export const printBatchLabels = async (items) => {
  try {
    const allLabels = [];
    items.forEach(item => {
      const copies = item.copies || 1;
      for (let i = 0; i < copies; i++) {
        allLabels.push(item);
      }
    });
    if (allLabels.length === 0) return;
    await queueAndPrintLabels(allLabels);
  } catch (err) {
    console.error('Batch label print error:', err);
    alert('Label printing failed: ' + err.message);
  }
};

// Legacy export for HardwareSettings compatibility
export const printLabels = async (labelHTML) => {
  const ok = await connectQZ();
  if (!ok || !PRINTER_MAP.label) {
    toast.error('Label printer not connected. Make sure QZ Tray is running.');
    return;
  }
  try {
    const config = qz.configs.create(PRINTER_MAP.label);
    await qz.print(config, [{ type: 'html', format: 'plain', data: labelHTML }]);
  } catch (err) {
    console.error('QZ label print failed:', err);
    toast.error('Label printing failed: ' + err.message);
  }
};

// Print document to A4 printer — opens new window, prints, closes
export const printDocument = async (documentHTML) => {
  const printWindow = window.open('', '_blank', 'width=800,height=600');
  if (printWindow) {
    printWindow.document.open();
    printWindow.document.write(documentHTML);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      setTimeout(() => { try { printWindow.close(); } catch(e) {} }, 3000);
    }, 300);
  }
};

